import React, { useState, useEffect } from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import "../../css/Common_New_Header.css";
import { getStorage } from "../../Utils/common";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import Lottie from "lottie-react";
import SecurityModal from "../SecurityModal";

export const Header = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [scrolled, setScrolled] = useState(false);
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });

  useEffect(() => {
    const token = getStorage("token");
    if (token) setIsLoggedIn(true);

    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMenuOpen]);

  const closeMenu = (e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    setIsMenuOpen(false);
  };

  const toggleMenu = (e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    setIsMenuOpen(prev => !prev);
  };

  const glassVariants = {
    floating: {
      width: isMobile ? "92%" : "85%",
      top: isMobile ? "15px" : "30px",
      borderRadius: isMobile ? "20px" : "100px",
      background: "rgba(255, 255, 255, 0.12)",
      backdropFilter: "blur(20px)",
      boxShadow: "0 20px 40px rgba(0,0,0,0.1), inset 0 0 0 1px rgba(255,255,255,0.3)"
    },
    docked: {
      width: "100%",
      top: "0px",
      borderRadius: "0px 0px 24px 24px",
      background: "rgba(255, 255, 255, 0.95)", // Light background for dark logo
      backdropFilter: "blur(40px)",
      boxShadow: "0 10px 40px rgba(0,0,0,0.1), inset 0 0 0 1px rgba(0,0,0,0.05)"
    }
  };

  return (
    <>
      <motion.header
        layout
        variants={glassVariants}
        animate={scrolled ? "docked" : "floating"}
        transition={{ type: "spring", stiffness: 120, damping: 15 }}
        className={`glossy_header ${scrolled ? "scrolled_header" : ""}`}
      >
        <div className="gloss_reflection"></div>

        <nav className="header_nav_gloss">
          <motion.div
            className="logo_gloss"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link to="/" onClick={closeMenu}>
              <img src={process.env.PUBLIC_URL + "/images/logo-dark.svg"} alt="logo" className="img_logo_gloss" width="850" height="200" fetchpriority="high" />
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <ul className="nav_list_gloss desktop_only">
            {[
              { name: "home", path: "/" },
              { name: "about", path: "/about-ActoLoan" },
              { name: "Why Us", path: "/ActoLoan-Services" },
              { name: "contact", path: "/contact-customer-care-ActoLoan" }
            ].map((item) => (
              <motion.li key={item.name} whileHover={{ y: -3 }} className="nav_li_wrapper">
                <NavLink
                  to={item.path}
                  end={item.path === "/"}
                  onClick={(e) => {
                    if (item.isScrollable && window.location.pathname === '/') {
                      e.preventDefault();
                      document.getElementById('faq-section')?.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className={({ isActive }) => `nav_item_gloss ${isActive ? "active" : ""}`}
                >
                  {({ isActive }) => (
                    <>
                      {item.name.toUpperCase()}
                      {isActive && (
                        <motion.div
                          className="dot_indicator"
                          layoutId="activeDot"
                          transition={{ type: "spring", stiffness: 380, damping: 30 }}
                        />
                      )}
                    </>
                  )}
                </NavLink>
              </motion.li>
            ))}
          </ul>

          <div className="actions_gloss desktop_only">
            {isLoggedIn ? (
              <motion.button
                className="gloss_btn primary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate("/my-dashboard")}
              >
                DASHBOARD
              </motion.button>
            ) : (
              <motion.div whileHover={{ scale: 1.05 }}>
                <NavLink to="/apply-now" className="gloss_btn primary">APPLY NOW</NavLink>
              </motion.div>
            )}
            <button onClick={() => setShowSecurityModal(true)} className="gloss_btn secondary">REPAY</button>
          </div>

          {/* Modern Mobile Burger */}
          <div className="burger_gloss" onClick={toggleMenu}>
            <div className={`burger_box_gloss ${isMenuOpen ? "active" : ""}`}>
              <span className="line_top"></span>
              <span className="line_mid"></span>
              <span className="line_bot"></span>
            </div>
          </div>
        </nav>

        {/* Progress Bar */}
        <motion.div className="gloss_progress" style={{ scaleX }} />
      </motion.header>

      {/* Full-Screen Premium Mobile Menu - Moved outside header to avoid clipping */}
      <AnimatePresence mode="wait">
        {isMenuOpen && (
          <motion.div
            key="mobile-menu"
            className="liquid_menu"
            initial={{ opacity: 0, clipPath: "circle(0% at 92% 35px)" }}
            animate={{ opacity: 1, clipPath: "circle(150% at 92% 35px)" }}
            exit={{ opacity: 0, clipPath: "circle(0% at 92% 35px)" }}
            transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
          >
            {/* Dedicated Close Button */}
            <div className="menu_close_btn" onClick={closeMenu}>
              <span></span>
              <span></span>
            </div>

            <div className="liquid_nav_container">
              <motion.div
                className="liquid_links"
                initial="closed"
                animate="open"
                variants={{
                  open: {
                    transition: { staggerChildren: 0.12, delayChildren: 0.4 }
                  }
                }}
              >
                {[
                  { name: "home", path: "/" },
                  { name: "about", path: "/about-ActoLoan" },
                  { name: "Why Us", path: "/ActoLoan-Services" },
                  { name: "contact", path: "/contact-customer-care-ActoLoan" }
                ].map((item, idx) => (
                  <motion.div
                    key={item.name}
                    variants={{
                      closed: { x: -80, opacity: 0, skewX: 10 },
                      open: { x: 0, opacity: 1, skewX: 0 }
                    }}
                    transition={{ duration: 0.8, ease: [0.6, 0.01, -0.05, 0.95] }}
                  >
                    <NavLink
                      to={item.path}
                      end={item.path === "/"}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (item.isScrollable && window.location.pathname === '/') {
                          e.preventDefault();
                          document.getElementById('faq-section')?.scrollIntoView({ behavior: 'smooth' });
                        }
                        closeMenu(e);
                      }}
                      className="liquid_link_item"
                    >
                      {({ isActive }) => (
                        <>
                          <span className="link_number">0{idx + 1}</span>
                          {item.name.toUpperCase()}
                          {isActive && (
                            <motion.div
                              className="mobile_dot"
                              layoutId="mobileActiveDot"
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ type: "spring", stiffness: 300, damping: 30 }}
                            />
                          )}
                          <div className="link_hover_bar"></div>
                        </>
                      )}
                    </NavLink>
                  </motion.div>
                ))}
              </motion.div>

              <motion.div
                className="liquid_footer"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1, duration: 0.6 }}
              >
                <motion.div style={{ display: 'flex', flexDirection: 'column', gap: '15px', width: '100%' }}>
                  <NavLink to="/apply-now" className="gloss_btn primary full_width_mobile" onClick={closeMenu}>
                    APPLY NOW
                  </NavLink>
                  <motion.div
                    animate={{
                      boxShadow: ["0 0 0px rgba(40, 137, 166, 0)", "0 0 15px rgba(40, 137, 166, 0.6)", "0 0 0px rgba(40, 137, 166, 0)"]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    style={{ borderRadius: "50px" }}
                  >
                    <button className="gloss_btn secondary full_width_mobile" onClick={() => { closeMenu(); setShowSecurityModal(true); }}>
                      REPAY NOW
                    </button>
                  </motion.div>
                </motion.div>
                <div className="mobile_contact_info">
                  <p>support@actoloan.com</p>
                  <div className="social_flex">
                    <div className="s_dot"></div>
                    <div className="s_dot"></div>
                    <div className="s_dot"></div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <SecurityModal 
        isOpen={showSecurityModal} 
        onClose={() => setShowSecurityModal(false)} 
      />
    </>
  );
};

export default Header;
